import { listUserProductAccess } from '@deque/billing-utils';
import { Loader } from '@deque/cauldron-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Switch, useParams } from 'react-router-dom';

import RedirectWithToast from '~/common/components/RedirectWithToast';
import { ACTIVE_STATES, INTEGRATION_PRODUCT_SLUGS } from '~/common/constants';
import { useAuthContext } from '~/common/contexts/auth';
import { useEnterprises } from '~/common/contexts/enterprises';
import { toError } from '~/common/utils/toError';
import TemplateWizard from '../components/integrations/IntegrationTemplateWizard/TemplateWizard';
import ListIntegrationTemplates from '../components/integrations/ListIntegrationTemplates';
import useIntegrationConnection from '../hooks/useIntegrationConnection';
import useIntegrationProjects from '../hooks/useIntegrationProjects';
import useIntegrationTemplates from '../hooks/useIntegrationTemplates';

function IntegrationTemplates() {
  const { t } = useTranslation();
  const { connectionId } = useParams<{
    connectionId: string;
  }>();

  const {
    user,
    billingUser,
    loading: authLoading,
    error: authError
  } = useAuthContext();
  const {
    activeEnterprise,
    isAdmin,
    loading: enterpriseLoading,
    error: enterpriseError
  } = useEnterprises();

  const {
    data: connection,
    loading: connectionLoading,
    error: connectionError
  } = useIntegrationConnection(
    connectionId,
    activeEnterprise?.id ?? '',
    user?.token ?? ''
  );

  const {
    data: templates,
    loading: templatesLoading,
    error: templatesError,
    refresh: refreshTemplates
  } = useIntegrationTemplates(
    connectionId,
    activeEnterprise?.id ?? '',
    user?.token ?? ''
  );

  const {
    data: projects,
    getProjectById,
    getIssueTypeById,
    getIssueTypesByProjectId,
    loading: projectsLoading,
    error: projectsError
  } = useIntegrationProjects(
    activeEnterprise?.id ?? '',
    connectionId,
    user?.token ?? ''
  );

  const baseUrl = `/configuration/integrations/:connectionId/templates`;

  const loading =
    authLoading ||
    enterpriseLoading ||
    connectionLoading ||
    templatesLoading ||
    projectsLoading;

  if (loading) {
    return <Loader label={t('Loading...')} tabIndex={-1} />;
  }

  const error =
    toError(authError) ||
    toError(enterpriseError) ||
    connectionError ||
    templatesError ||
    projectsError;

  const isSomeDataAbsent =
    !user || !billingUser || !activeEnterprise || !templates || !projects;

  const canAccessIntegrations =
    billingUser &&
    INTEGRATION_PRODUCT_SLUGS.filter(slug =>
      ACTIVE_STATES.includes(listUserProductAccess(billingUser)[slug])
    ).length > 0;

  if (!canAccessIntegrations) {
    return (
      <RedirectWithToast
        type="caution"
        message={t(
          'A Jira subscription is required. Please contact your administrator to get provisioned.'
        )}
        to="/configuration/integrations"
      />
    );
  }

  if (!connection) {
    return (
      <RedirectWithToast
        type="error"
        message={t('Failed to load the connection.')}
        to="/configuration/integrations"
      />
    );
  }
  if (connection.status !== 'connected') {
    return (
      <RedirectWithToast
        type="caution"
        message={t('This Jira connection is not active.')}
        to="/configuration/integrations"
      />
    );
  }

  if (error || isSomeDataAbsent || !connection?.base_url) {
    const message =
      error instanceof Error ? error.message : t('some data absent');
    return <div>{t(`Error: {{ message }}`, { message })}</div>;
  }

  const templateProps = {
    templates,
    connectionId,
    getProjectById,
    getIssueTypeById,
    refreshTemplates,
    token: user.token,
    enterpriseId: activeEnterprise.id,
    integrationProductSlug: connection.product_slug,
    instanceUrl: connection.base_url
  };

  return (
    <Switch>
      <Route exact path={baseUrl}>
        <ListIntegrationTemplates
          {...templateProps}
          canManageIntegrations={!!canAccessIntegrations && isAdmin}
          userKeycloakId={billingUser.keycloak_id}
        />
      </Route>
      <Route exact path={`${baseUrl}/new`}>
        <TemplateWizard
          {...templateProps}
          projects={projects}
          getIssueTypesByProjectId={getIssueTypesByProjectId}
        />
      </Route>
      <Route path={`${baseUrl}/:id`}>
        <TemplateWizard
          {...templateProps}
          projects={projects}
          getIssueTypesByProjectId={getIssueTypesByProjectId}
          isEditMode={true}
          isUserAdmin={isAdmin}
          userKeycloakId={billingUser.keycloak_id}
        />
      </Route>
    </Switch>
  );
}

export default IntegrationTemplates;
